import React, { useMemo, useState } from 'react';
import { Card, Typography, Alert, Space, Grid, Button, Upload } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { SelfRegistrationSessionDTO } from '@/shared/api/model';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import { useGetSession } from '@/shared/api/client/self-registration';
import { useGetActiveConfig } from '@/modules/onboarding/hooks/useSelfOnboardingConfig';
import { useSelfRegistration } from '../../hooks/useSelfRegistration';
import { showErrorNotification, showSuccessNotification } from '@/components/common/utils/notify';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

interface DeductionFormStepProps {
    session: SelfRegistrationSessionDTO;
    onComplete: (session: SelfRegistrationSessionDTO) => void;
    onError: (error: string) => void;
}

const DeductionFormStep: React.FC<DeductionFormStepProps> = ({ session, onComplete, onError }) => {
    const screens = useBreakpoint();
    const isMobile = !screens.md;

    const { uploadDocument, isUploadingDocument } = useSelfRegistration();
    const { data: activeConfig } = useGetActiveConfig();
    const [fileList, setFileList] = useState<UploadFile[]>([]);

    const sessionQuery = useGetSession(session.sessionToken || '', {
        query: {
            enabled: !!session.sessionToken,
            refetchInterval: 30000,
            staleTime: 10000
        }
    });

    const currentSession = useMemo(() => {
        const raw = sessionQuery.data?.data as any;
        return (raw?.data || raw || session) as SelfRegistrationSessionDTO;
    }, [sessionQuery.data, session]);

    const documents = (currentSession as any)?.uploadedDocuments || [];
    const officialDeductionForm = documents.find(
        (d: any) => (d?.documentType || '').toUpperCase() === 'OFFICIAL_DEDUCTION_FORM'
    );
    const signedDeductionForm = documents.find(
        (d: any) => (d?.documentType || '').toUpperCase() === 'SIGNED_DEDUCTION_FORM'
    );
    const requiresMemberSignedUpload = activeConfig?.requiresMemberSignedDeductionFormUpload !== false;

    const openDocument = (doc: any, download: boolean) => {
        if (!currentSession?.sessionToken || !doc?.id) return;
        const url = `/frontend/v1/self-registration/document/${currentSession.sessionToken}/${doc.id}?download=${download}`;
        window.open(url, '_blank');
    };

    const uploadProps: UploadProps = {
        multiple: false,
        fileList,
        beforeUpload: () => false,
        onChange: (info) => {
            setFileList(info.fileList.slice(-1));
        },
        onRemove: () => {
            setFileList([]);
        }
    };

    const handleUploadSigned = async () => {
        try {
            const uploadFile = fileList?.[0];
            const file = (uploadFile?.originFileObj || (uploadFile as any)) as File | undefined;
            if (!currentSession?.sessionToken) {
                showErrorNotification('Missing session token');
                return;
            }
            if (!officialDeductionForm) {
                showErrorNotification('Official deduction form is not yet available');
                return;
            }
            if (!file) {
                showErrorNotification('Please choose a file to upload');
                return;
            }

            await uploadDocument(currentSession.sessionToken, 'SIGNED_DEDUCTION_FORM', file);
            showSuccessNotification('Signed deduction form uploaded successfully');
            setFileList([]);

            const refreshed = await sessionQuery.refetch();
            const refreshedResponse = refreshed.data as any;
            const refreshedSession = (refreshedResponse?.data?.data || refreshedResponse?.data || refreshedResponse) as SelfRegistrationSessionDTO | undefined;
            if (refreshedSession) {
                onComplete(refreshedSession);
            }
        } catch (e: any) {
            const msg = e?.response?.data?.message || e?.message || 'Failed to upload signed deduction form';
            showErrorNotification(msg);
            onError(msg);
        }
    };

    return (
        <Card 
            title={
                <Space direction="vertical" size={0}>
                    <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>
                        Monthly Deduction Authorization Form
                    </Title>
                    <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>
                        {requiresMemberSignedUpload
                            ? 'Download, sign and upload the form to proceed.'
                            : 'Download the form, get it signed, and keep the signed copy for your records.'}
                    </Text>
                </Space>
            }
            style={{ maxWidth: 800, margin: '0 auto' }}
        >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {!officialDeductionForm ? (
                    <Alert
                        message="Important Instructions"
                        description={
                            <ol style={{ paddingLeft: isMobile ? 16 : 20, margin: 0 }}>
                                <li>The officer will upload the official deduction form for you to download</li>
                                <li>Once available, download it and get it signed</li>
                                <li>{requiresMemberSignedUpload ? 'Upload the signed form here for officer review' : 'Keep the signed form copy with you; no upload is required here'}</li>
                                <li>This page refreshes automatically every 30 seconds</li>
                            </ol>
                        }
                        type="info"
                        showIcon
                    />
                ) : (
                    <Alert
                        message="Important Instructions"
                        description={
                            <ol style={{ paddingLeft: isMobile ? 16 : 20, margin: 0 }}>
                                <li>Download the official deduction form below</li>
                                <li>Get it signed by your employer</li>
                                <li>{requiresMemberSignedUpload ? 'Upload the signed deduction form to submit it for review' : 'Keep the signed deduction form; your registration will be completed once the officer finalizes the process'}</li>
                            </ol>
                        }
                        type="info"
                        showIcon
                    />
                )}

                {officialDeductionForm && (
                    <Card size={isMobile ? 'small' : 'default'}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Text strong>Official Deduction Form</Text>
                            <Space wrap>
                                <Button onClick={() => openDocument(officialDeductionForm, false)}>
                                    View
                                </Button>
                                <Button type="primary" onClick={() => openDocument(officialDeductionForm, true)}>
                                    Download
                                </Button>
                            </Space>

                            {requiresMemberSignedUpload && !signedDeductionForm && (
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Text strong>Upload Signed Deduction Form</Text>
                                    <Upload {...uploadProps}>
                                        <Button block>Select file</Button>
                                    </Upload>
                                    <Button
                                        type="primary"
                                        block
                                        onClick={handleUploadSigned}
                                        loading={isUploadingDocument}
                                        disabled={fileList.length === 0}
                                    >
                                        Upload Signed Form
                                    </Button>
                                </Space>
                            )}

                            {!requiresMemberSignedUpload && (
                                <Alert
                                    message="No Upload Required"
                                    description="Your institution does not require you to upload the signed deduction form back. Keep the signed copy for your records."
                                    type="success"
                                    showIcon
                                />
                            )}
                        </Space>
                    </Card>
                )}

                {signedDeductionForm && (
                    <Card size={isMobile ? 'small' : 'default'}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Text strong>Signed Deduction Form</Text>
                            <Space wrap>
                                <Button onClick={() => openDocument(signedDeductionForm, false)}>
                                    View
                                </Button>
                                <Button onClick={() => openDocument(signedDeductionForm, true)}>
                                    Download
                                </Button>
                            </Space>
                        </Space>
                    </Card>
                )}

                {currentSession.status === 'PENDING_DEDUCTION_FORM_REVIEW' && (
                    <Alert
                        message="Under Review"
                        description="Your signed deduction form has been submitted and is currently under review by our officers. You will be notified once it's approved."
                        type="warning"
                        showIcon
                        icon={<CheckCircleOutlined />}
                    />
                )}
            </Space>
        </Card>
    );
};

export default DeductionFormStep;
